/**
 * LeftLabeledLevelsPrimitive — horizontal price levels drawn across the full pane
 * width, each with its text label pinned to the LEFT edge (inside a small pill).
 *
 * Used for the option-basket payoff levels (Take-Profit / Stop-Loss / Break-even /
 * Max-profit). lightweight-charts' createPriceLine() can only put its label on the
 * RIGHT price axis, where it collided with the FNO option-profile rail; drawing the
 * labels on the left keeps the right-side profiles fully readable.
 */
import type {
  ISeriesPrimitive,
  IPrimitivePaneView,
  IPrimitivePaneRenderer,
  SeriesAttachedParameter,
  Time,
  IChartApi,
  ISeriesApi,
  SeriesType,
} from 'lightweight-charts';
import type { CanvasRenderingTarget2D } from 'fancy-canvas';

export interface LLevel {
  price: number;
  color: string;
  /** Left-pinned text label, e.g. "TP↑ 1,18,500". */
  label: string;
  /** 0 solid, 2 dashed (lightweight-charts LineStyle ints). */
  lineStyle?: number;
  /** When true, render ONLY the left-pinned label (no line) — for prices that already have a
   *  draggable line drawn by the right-axis price-levels primitive (avoids a doubled line). */
  noLine?: boolean;
}

class LLevelsRenderer implements IPrimitivePaneRenderer {
  constructor(
    private readonly levels: LLevel[],
    private readonly series: ISeriesApi<SeriesType>,
    /** px from the top to start stacking labels, so they clear a top-left badge. */
    private readonly topInset: number,
  ) {}

  draw(target: CanvasRenderingTarget2D): void {
    if (!this.levels.length) return;
    const series = this.series;
    target.useBitmapCoordinateSpace(({ context: ctx, bitmapSize, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      ctx.save();
      const rightX = bitmapSize.width;
      const fontPx = Math.round(10.5 * Math.min(hpr, vpr));
      ctx.font = `800 ${fontPx}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'left';
      for (const lv of this.levels) {
        const yC = series.priceToCoordinate(lv.price);
        if (yC == null) continue;
        const y = Number(yC) * vpr;
        // The level line across the pane (skipped when a draggable line already exists here).
        if (!lv.noLine) {
          const w = Math.max(1, 1.5 * Math.min(hpr, vpr));
          ctx.lineWidth = w;
          ctx.strokeStyle = lv.color;
          ctx.setLineDash(lv.lineStyle === 2 ? [5 * hpr, 4 * hpr] : []);
          ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(rightX, y); ctx.stroke();
          ctx.setLineDash([]);
        }
        // Left-pinned label pill.
        const padX = 5 * hpr, padY = 3 * vpr;
        const tw = ctx.measureText(lv.label).width;
        const x0 = 4 * hpr;
        const boxH = fontPx + padY * 2;
        const boxW = tw + padX * 2;
        const r = 3 * Math.min(hpr, vpr);
        ctx.beginPath();
        ctx.moveTo(x0 + r, y - boxH / 2);
        ctx.arcTo(x0 + boxW, y - boxH / 2, x0 + boxW, y + boxH / 2, r);
        ctx.arcTo(x0 + boxW, y + boxH / 2, x0, y + boxH / 2, r);
        ctx.arcTo(x0, y + boxH / 2, x0, y - boxH / 2, r);
        ctx.arcTo(x0, y - boxH / 2, x0 + boxW, y - boxH / 2, r);
        ctx.closePath();
        ctx.fillStyle = lv.color;
        ctx.fill();
        ctx.fillStyle = '#ffffff';
        ctx.fillText(lv.label, x0 + padX, y + 0.5 * vpr);
      }
      ctx.restore();
    });
  }
}

class LLevelsPaneView implements IPrimitivePaneView {
  constructor(private readonly parent: LeftLabeledLevelsPrimitive) {}
  zOrder() { return 'top' as const; }
  renderer(): IPrimitivePaneRenderer | null {
    const series = this.parent.series;
    if (!series) return null;
    return new LLevelsRenderer(this.parent.levels, series, this.parent.topInset);
  }
}

export class LeftLabeledLevelsPrimitive implements ISeriesPrimitive<Time> {
  levels: LLevel[] = [];
  topInset = 0;
  chart: IChartApi | null = null;
  series: ISeriesApi<SeriesType> | null = null;
  private readonly _paneViews: LLevelsPaneView[];

  constructor(levels: LLevel[], topInset = 0) {
    this.levels = levels;
    this.topInset = topInset;
    this._paneViews = [new LLevelsPaneView(this)];
  }

  attached(params: SeriesAttachedParameter<Time>): void {
    this.chart = params.chart as IChartApi;
    this.series = params.series as ISeriesApi<SeriesType>;
  }
  detached(): void { this.chart = null; this.series = null; }

  updateAllViews(): void { /* renderer reads this.levels fresh each draw */ }
  paneViews(): readonly IPrimitivePaneView[] { return this._paneViews; }

  setData(levels: LLevel[]): void {
    this.levels = levels;
    this.chart?.applyOptions({});
  }
}
